import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion';
import {useUser} from '../../contexts/UserContext';
import {patientDisplayNameFromRecord} from '../../utils/patientNames';
import {TablerX} from '../../icons';
import CreateSessionForm from './CreateSessionForm';
import {CREATE_SESSION_CARD_PADDING} from './CreateSessionFormSections';
import PlannerPatientPanel from './PlannerPatientPanel';
import PlannerBrowserColumn from './PlannerBrowserColumn';
import PhoneCallDetailPanel from '../session/PhoneCallDetailPanel';
import TelephoneEncounterDetailView from '../telephone/TelephoneEncounterDetailView';
import EncounterDetailFrame from '../common/EncounterDetailFrame';
import ViewLoader from '../common/ViewLoader';
import usePlannerStyles, {useScrollbarSx} from './usePlannerStyles';
import usePlannerEventSections from './usePlannerEventSections';
import {buildPlannerPatientSelectionEvent} from './plannerPatientBrowser';
import {
  clean,
  plannerPatientToPatientItem,
  plannerEventFromTelephoneEncounter,
} from './plannerModels';
import {telephoneEncounterService} from '../../services/telephoneEncounterService';
import {SCRIBE_ROUTE} from '../../utils/scribeLaunch';
import {APP_ROUTES} from '../appShellRoutes';
import {TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE} from '../../services/encounterUtils';
import {plannerEventDate} from './plannerDateTime';
import {applyCachedEhrDemographics} from './useHydratedEhrPatients';
import {ehrService} from '../../services/ehrService';
import {useSameDayEhrSync} from '../../hooks/useSameDayEhrSync';
import {useEhrLinked} from '../../hooks/useEhrLinked';
import {PLANNER_MOTION_EASE, PLANNER_MOTION_EASE_CSS} from './plannerMotion';
import {isMockEhrEnabled} from '../../mocks/mockEhrFixtures';
import {clearPlannerEncounterHistoryPageCache} from './usePaginatedPlannerEncounterHistory';

const SessionDetailPanel = lazy(() => import('../session/SessionDetailPanel'));
const ScheduledSessionDetailPanel = lazy(
  () => import('../session/ScheduledSessionDetailPanel'),
);
const PLANNER_HOME_SCROLL_BOTTOM_GAP = '4em';
const PLANNER_HOME_SCROLL_REVEAL_THRESHOLD_PX = 1;
const PLANNER_HOME_SCROLL_SETTLE_DELAY_MS = 220;
const PLANNER_HOME_SCROLL_DURATION_MS = 200;
const PLANNER_HOME_MOTION_EASE = PLANNER_MOTION_EASE;
const PLANNER_HOME_MOTION_EASE_CSS = PLANNER_MOTION_EASE_CSS;
const PLANNER_HOME_SCROLL_EASE = PLANNER_HOME_MOTION_EASE;
const PLANNER_HOME_SCROLL_TARGET_GAP_EM = 2;
const PLANNER_TOP_ACTION_HEIGHT = '44px';
const PLANNER_CONTENT_MAX_WIDTH = '1000px';
// On wide displays the right column grows without bound, so a fixed cap leaves
// the content marooned in the middle with a broken-looking gap next to the
// sidebar. Let it scale with the viewport instead: it fills mid-size monitors
// edge-to-edge and only centers (with balanced margins) on very large ones.
const PLANNER_CONTENT_MAX_WIDTH_WIDE = 'clamp(60rem, 78vw, 82rem)';
// Horizontal breathing room used by the planner detail column. When the
// encounter detail goes full-bleed (scrollbar at the panel edge), this is the
// inset re-applied *inside* the scroller so the note content keeps the same
// right margin it had before while the scrollbar sits flush against the edge.
const PLANNER_DETAIL_EDGE_INSET = 'clamp(2rem, 3vw, 4rem)';
const PLANNER_MODAL_CLOSE_BUTTON_SIZE = '1.5rem';
const PLANNER_MODAL_CLOSE_BUTTON_TOP = {
  base: 'calc(1rem + 0.4375rem)',
  md: 'calc(1.25rem + 0.4375rem)',
};
const PLANNER_MODAL_CLOSE_BUTTON_X_ALIGN_OFFSET = '0.25rem';

const plannerHomeScrollTargetGapPx = (element: HTMLElement | null) => {
  if (!element || typeof window === 'undefined') return 8;
  const fontSize = Number.parseFloat(
    window.getComputedStyle(element).fontSize || '',
  );
  return (
    (Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 16) *
    PLANNER_HOME_SCROLL_TARGET_GAP_EM
  );
};

const cubicBezierCoordinate = (t: number, p1: number, p2: number) => {
  const inverseT = 1 - t;
  return (
    3 * inverseT * inverseT * t * p1 + 3 * inverseT * t * t * p2 + t * t * t
  );
};

const cubicBezierDerivative = (t: number, p1: number, p2: number) => {
  const inverseT = 1 - t;
  return (
    3 * inverseT * inverseT * p1 +
    6 * inverseT * t * (p2 - p1) +
    3 * t * t * (1 - p2)
  );
};

const plannerHomeScrollEase = (progress: number) => {
  const [x1, y1, x2, y2] = PLANNER_HOME_SCROLL_EASE;
  let t = progress;
  for (let index = 0; index < 5; index += 1) {
    const x = cubicBezierCoordinate(t, x1, x2) - progress;
    const derivative = cubicBezierDerivative(t, x1, x2);
    if (Math.abs(x) < 0.0001 || derivative === 0) break;
    t = Math.min(1, Math.max(0, t - x / derivative));
  }
  return cubicBezierCoordinate(t, y1, y2);
};

const isEmailLike = (value: any) => /\S+@\S+\.\S+/.test(clean(value));

const firstNonEmail = (...values: any[]) =>
  values.map(clean).find(value => value && !isEmailLike(value)) || '';

const providerNameFromUserData = (userDataInput: any) => {
  const userData = userDataInput || {};
  const joinedName = firstNonEmail(
    [userData.firstName, userData.lastName]
      .map(clean)
      .filter(Boolean)
      .join(' '),
    [userData.first_name, userData.last_name]
      .map(clean)
      .filter(Boolean)
      .join(' '),
  );

  return firstNonEmail(
    userData.providerName,
    userData.provider_name,
    userData.displayName,
    userData.fullName,
    userData.name,
    joinedName,
  );
};

const timeSensitiveGreeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const upcomingAppointmentCount = (sections: any[] = []) => {
  const upcomingSection = sections.find(
    section =>
      section.key === 'upcoming' ||
      String(section.label || '').toLowerCase() === 'upcoming',
  );
  if (upcomingSection) return upcomingSection.events?.length || 0;
  return sections.reduce(
    (count, section) =>
      count +
      (section.events || []).filter((event: any) => event?.stage === 'upcoming')
        .length,
    0,
  );
};

const splitScheduledPatientName = (value: any) => {
  const name = clean(value);
  if (!name || /^unknown patient$/i.test(name))
    return {firstName: '', lastName: ''};
  const parts = name.split(/\s+/);
  if (parts.length === 1) return {firstName: parts[0], lastName: ''};
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

const scheduledFallbackId = (patientName: any, fallbackId: any) =>
  clean(fallbackId) ||
  `scheduled_${clean(patientName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')}`;

const applyScheduledPatientNameFallback = (
  item: any,
  patientName: any,
  fallbackId: any = '',
) => {
  const name = clean(patientName);
  const {firstName, lastName} = splitScheduledPatientName(name);
  if (!firstName) return null;
  const baseItem = item || {
    id: scheduledFallbackId(name, fallbackId),
    matchKind: 'scheduledOnly',
    localPatient: null,
    ehrPatient: null,
  };

  return {
    ...baseItem,
    patientName: name,
    displayName: name,
    sortName: name.toLowerCase(),
    localPatient: baseItem.localPatient
      ? {
          ...baseItem.localPatient,
          first_name: baseItem.localPatient.first_name || firstName,
          last_name: baseItem.localPatient.last_name || lastName,
        }
      : null,
    ehrPatient: baseItem.ehrPatient
      ? {
          ...baseItem.ehrPatient,
          firstName: baseItem.ehrPatient.firstName || firstName,
          lastName: baseItem.ehrPatient.lastName || lastName,
        }
      : null,
  };
};

export const buildScheduledTodayPatientItems = (sections: any[] = []) => {
  const items: any[] = [];
  sections.forEach(section => {
    (section?.events || []).forEach((event: any) => {
      if (!['appointment', 'scheduledBot'].includes(clean(event?.source)))
        return;
      const item = applyScheduledPatientNameFallback(
        plannerPatientToPatientItem(event?.patient),
        event?.patientName ||
          event?.patient?.displayName ||
          event?.patient?.fullName,
        event?.id || event?.sourceId,
      );
      if (!item) return;
      items.push({
        ...item,
        scheduledEventId: clean(event?.id || event?.sourceId),
        scheduledSortDate: plannerEventDate(event),
      });
    });
  });
  return items;
};

const MotionBox = motion(Box);

const HOME_ENTRANCE_ITEM_DURATION_MS = 220;
const RIGHT_PANEL_PAGE_FADE_DURATION_MS = 220;
const HOME_ENTRANCE_MOTION_DATA_ATTRS = {
  'data-motion-duration': `${HOME_ENTRANCE_ITEM_DURATION_MS}ms`,
  'data-motion-ease': PLANNER_HOME_MOTION_EASE_CSS,
};
const RIGHT_PANEL_PAGE_FADE_DATA_ATTRS = {
  'data-motion-kind': 'fade',
  'data-motion-duration': `${RIGHT_PANEL_PAGE_FADE_DURATION_MS}ms`,
  'data-motion-ease': PLANNER_HOME_MOTION_EASE_CSS,
};

// Entrance for the right-hand detail column. Re-triggered on every new
// selection (the wrapper is keyed on the event id) so the panel change is
// visible without sliding the shell in from the side.
const PLANNER_DETAIL_ENTRANCE_VARIANTS = {
  hidden: {opacity: 0},
  visible: {
    opacity: 1,
    transition: {
      duration: RIGHT_PANEL_PAGE_FADE_DURATION_MS / 1000,
      ease: PLANNER_HOME_MOTION_EASE,
    },
  },
  // Clearing the detail (the Clear button, Back, etc.) fades the panel out on
  // the same curve it entered on, rather than vanishing in a single frame.
  exit: {
    opacity: 0,
    transition: {
      duration: RIGHT_PANEL_PAGE_FADE_DURATION_MS / 1000,
      ease: PLANNER_HOME_MOTION_EASE,
    },
  },
};

const PlannerStartSummary = ({
  userData,
  sections,
  styles,
  itemProps,
}: Record<string, any>) => {
  const providerName = providerNameFromUserData(userData);
  const greeting = timeSensitiveGreeting();
  const greetingText = providerName ? `${greeting}, ${providerName}` : greeting;
  const appointmentCount = upcomingAppointmentCount(sections);
  const appointmentWord =
    appointmentCount === 1 ? 'appointment' : 'appointments';

  return (
    <Box mb={{base: 5, md: '2em'}}>
      <MotionBox
        as="h1"
        color={styles.textColor}
        fontSize={{base: '2xl', md: '3xl'}}
        fontWeight="semibold"
        lineHeight="1.15"
        {...HOME_ENTRANCE_MOTION_DATA_ATTRS}
        {...itemProps}
      >
        {greetingText}
      </MotionBox>
      <MotionBox
        as="h2"
        color={styles.subtleText}
        fontSize={{base: 'md', md: 'lg'}}
        fontWeight="normal"
        lineHeight="1.4"
        mt={2}
        {...itemProps}
      >
        {`You have ${appointmentCount} ${appointmentWord} today.`}
      </MotionBox>
    </Box>
  );
};

const PlannerUnlinkedDetailFrame = ({
  children,
  headerTitle = 'Unlinked Patient',
  onClose,
  onLinkPatient = null,
  headerPr = 0,
}: Record<string, any>) => (
  <EncounterDetailFrame
    onBack={onClose}
    backLabel="Back to encounters"
    backTestId="planner-unlinked-back"
    showHeader={headerTitle !== null}
    unlinkedTitle={headerTitle || 'Unlinked Patient'}
    onLinkPatient={onLinkPatient}
    headerPr={headerPr}
  >
    {children}
  </EncounterDetailFrame>
);

const PlannerPage = () => {
  const {userData} = useUser();
  const isEhrLinked = useEhrLinked();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [unlinkedPatientSelectorRequest, setUnlinkedPatientSelectorRequest] =
    useState<any>(null);
  const [newEncounterFormKey, setNewEncounterFormKey] = useState(0);
  const [recentsFeedRevision, setRecentsFeedRevision] = useState(0);
  const homeScrollControllerRef = useRef<any>(null);
  const homeNewEncounterSectionRef = useRef<any>(null);
  const homeScrollAnimationRef = useRef<number | null>(null);
  const homeNewEncounterSectionHeightRef = useRef<number | null>(null);
  const styles = usePlannerStyles();
  const scrollbarSx = useScrollbarSx();
  const {pageBg} = styles;
  const prefersReducedMotion = useReducedMotion();
  // Home reuses the same fade as the detail panels so swapping between the
  // home form and a selected patient is one continuous cross-fade rather than a
  // fade colliding with a separate staggered slide-in.
  const homeEntrance = prefersReducedMotion
    ? {container: RIGHT_PANEL_PAGE_FADE_DATA_ATTRS, item: {}}
    : {
        container: {
          ...RIGHT_PANEL_PAGE_FADE_DATA_ATTRS,
          variants: PLANNER_DETAIL_ENTRANCE_VARIANTS,
          initial: 'hidden',
          animate: 'visible',
          exit: 'exit',
        },
        item: {},
      };
  // The detail panels overlay the scroll column (absolute, inset 0) so a panel
  // fading out on Clear/Back lifts off cleanly over the restored home content
  // instead of fighting it for layout space mid-animation.
  const detailEntrance = prefersReducedMotion
    ? {position: 'absolute', inset: 0, ...RIGHT_PANEL_PAGE_FADE_DATA_ATTRS}
    : {
        position: 'absolute',
        inset: 0,
        ...RIGHT_PANEL_PAGE_FADE_DATA_ATTRS,
        variants: PLANNER_DETAIL_ENTRANCE_VARIANTS,
        initial: 'hidden',
        animate: 'visible',
        exit: 'exit',
      };
  const {
    isOpen: isNewEncounterOpen,
    onOpen: onNewEncounterOpen,
    onClose: onNewEncounterClose,
  } = useDisclosure();

  const {
    sections,
    scheduleScope,
    isLoading: isScheduleLoading,
    hasLoaded: hasScheduleLoaded,
    error: scheduleError,
    isFallback: shouldUseScaffoldFallback,
    refresh: refreshSchedule,
    encounterHistorySeed,
  } = usePlannerEventSections({userData});
  const isMockEhrMode = isMockEhrEnabled(userData);

  const {refreshNow: refreshEhrConnection} = useSameDayEhrSync({
    userId: userData?.userId,
    onNewAppointments: refreshSchedule,
    enabled: !isMockEhrMode,
  });

  const handleRefreshEhrConnection = useCallback(async () => {
    await refreshEhrConnection?.({notify: false});
    refreshSchedule?.();
  }, [refreshEhrConnection, refreshSchedule]);

  const handleRecentsFeedMutation = useCallback(() => {
    clearPlannerEncounterHistoryPageCache();
    setRecentsFeedRevision(previous => previous + 1);
    refreshSchedule?.();
  }, [refreshSchedule]);

  const selectedPatient = selectedEvent?.patient || null;

  useEffect(() => {
    const state = location.state;
    if (!state) return;
    const {restorePatientItem, restoreSession} = state;
    if (!restorePatientItem && !restoreSession) return;

    if (restorePatientItem) {
      const restoredEvent =
        buildPlannerPatientSelectionEvent(restorePatientItem);
      if (restoredEvent) {
        setSelectedEvent(restoredEvent);
      }
    }
    if (restoreSession) {
      setSelectedSession(restoreSession);
    }

    navigate(location.pathname, {replace: true, state: {}});
  }, [location.state, location.pathname, navigate]);

  const handleSelectEvent = useCallback((event: any) => {
    setSelectedSession(null);
    setUnlinkedPatientSelectorRequest(null);
    setSelectedEvent((prev: any) => {
      if (prev?.id !== event?.id) return event;
      return Boolean(prev?.__plannerOpenEncounterDetail) ===
        Boolean(event?.__plannerOpenEncounterDetail)
        ? prev
        : event;
    });
  }, []);

  const handleSelectPatient = useCallback(
    (patientItem: any, source: any) => {
      const event = buildPlannerPatientSelectionEvent(patientItem, {source});
      if (!event) return;
      setSelectedSession(null);
      setUnlinkedPatientSelectorRequest(null);
      setSelectedEvent((prev: any) => (prev?.id === event.id ? prev : event));

      // A live EHR search hit can still be "thin" (name only) at click time, and
      // this selection event is otherwise frozen — it never re-derives when the
      // browser row hydrates later. Resolve the patient's demographics on demand
      // so the detail panel fills language / DOB / MRN on the first click.
      //
      // Note: scheduled EHR patients commonly arrive with DOB/MRN already but no
      // language (it isn't on the appointment row), so a missing language alone
      // must still trigger a resolve — otherwise the New Encounter form never
      // auto-fills the interpreter language for EHR-linked patients.
      const {patient} = event;
      const needsDemographics =
        patient &&
        (!clean(patient.dob) ||
          !clean(patient.mrn) ||
          !clean(patient.language));
      const companyId = userData?.companyId || null;
      const ehrSourceId =
        patientItem?.ehrPatient?.ehrSourceId ||
        patientItem?.ehrPatient?.ehr_source_id ||
        patientItem?.ehrPatient?.id;
      if (isMockEhrMode || !needsDemographics || !companyId || !ehrSourceId)
        return;

      ehrService
        .resolveEhrPatients(companyId, [ehrSourceId])
        .then((map: Record<string, any>) => {
          const resolved = map?.[ehrSourceId];
          if (!resolved) return;
          const [enrichedEhr] = applyCachedEhrDemographics(
            [patientItem.ehrPatient],
            {[ehrSourceId]: resolved},
          );
          const enrichedEvent = buildPlannerPatientSelectionEvent(
            {...patientItem, ehrPatient: enrichedEhr},
            {source},
          );
          if (!enrichedEvent) return;
          // Only replace the panel if this patient is still the selection.
          setSelectedEvent((prev: any) =>
            prev?.id === enrichedEvent.id ? enrichedEvent : prev,
          );
        })
        .catch(() => {
          /* best-effort enrichment; leave the bare selection in place */
        });
    },
    [isMockEhrMode, userData],
  );

  const handlePatientUpdated = useCallback((updatedPatient: any) => {
    if (!updatedPatient?.id) return;
    setSelectedEvent((prev: any) => {
      if (!prev?.patient?.id || prev.patient.id !== updatedPatient.id) {
        return prev;
      }
      const patientName =
        patientDisplayNameFromRecord(updatedPatient) || prev.patientName;
      return {
        ...prev,
        patient: updatedPatient,
        patientName,
        language: updatedPatient.language || prev.language,
      };
    });
  }, []);

  // Ids of patients deleted this session, so the browser roster can drop them
  // instantly (a hard delete won't come back on the next fetch, but the roster
  // hook doesn't refetch on delete). Without this the deleted patient lingers
  // in the list, gets re-selected, and the second delete 404s — the "Failed to
  // delete patient" bug.
  const [deletedPatientIds, setDeletedPatientIds] = useState<Set<string>>(
    () => new Set(),
  );
  const handlePatientDeleted = useCallback((patientId: any) => {
    if (!patientId) return;
    // Only record the pruned id here; the panel's own delete handler already
    // closes the panel and calls onScheduled (refreshSchedule), so refreshing
    // here too would double-fire it.
    setDeletedPatientIds(prev => {
      const next = new Set(prev);
      next.add(String(patientId));
      return next;
    });
  }, []);

  const handleClearSelectedEvent = useCallback(() => {
    setSelectedSession(null);
    setUnlinkedPatientSelectorRequest(null);
    setSelectedEvent(null);
  }, []);

  const handleCreateEncounter = useCallback(() => {
    setSelectedSession(null);
    setSelectedEvent(null);
    setNewEncounterFormKey(previousKey => previousKey + 1);
    onNewEncounterOpen();
  }, [onNewEncounterOpen]);

  const openTelephoneEncounter = useCallback((te: any) => {
    const event = plannerEventFromTelephoneEncounter(te);
    if (!event) return;
    setSelectedSession(null);
    setUnlinkedPatientSelectorRequest(null);
    setSelectedEvent(event);
  }, []);

  // Documenting a call now happens in the unified Encounter Review: one flow
  // whose template step offers "Clinical note | Telephone encounter" for phone
  // visits. Deep-link into it with the telephone tab preselected and the
  // message already generating — same speed as the old planner-hosted TE form,
  // but with the clinical-note path one visible switch away. (The planner's
  // own TelephoneEncounterDetailView remains for blank TEs and for working
  // open encounters.)
  const openUnifiedTelephoneFlow = useCallback(
    (call: any) => {
      navigate(APP_ROUTES.process, {
        state: {
          conversationId: call.id,
          patientId: call.patient_id ?? null,
          ehrPatientId: call.ehr_patient_id ?? null,
          patientName: call.patient_name ?? null,
          language: call.language ?? '',
          dialect: call.dialect ?? '',
          sessionType: TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
          preferredDocType: 'telephone',
          teAutoGenerate: true,
          initialProcessStep: 'note',
          fromPlanner: true,
        },
      });
    },
    [navigate],
  );

  // Turn a completed company phone call into a telephone-encounter draft in
  // the unified flow. The open-TE "add to it vs keep new" check runs there.
  const handleCreateTelephoneEncounterFromCall = useCallback(
    async (call: any) => {
      openUnifiedTelephoneFlow(call);
    },
    [openUnifiedTelephoneFlow],
  );

  // Adopt an already-open telephone encounter (surfaced by the detail view's
  // patient check) and reopen it in EDIT mode.
  const handleAdoptOpenTelephoneEncounter = useCallback(
    async (summary: any, originatingCall?: any) => {
      const te = await telephoneEncounterService.adoptOpenTelephoneEncounter(
        summary,
        {
          companyId: userData?.companyId || null,
          ehrPatientId: summary?.ehrPatientId || null,
          patientName: summary?.patientName || null,
          createdBy: userData?.userId || null,
          createdByName:
            providerNameFromUserData(userData) || userData?.displayName || null,
          // Forward the call they just had so the adopted encounter drafts its
          // Action Taken note from that transcript.
          sourceCallId: originatingCall?.sourceCallId || null,
          translator: originatingCall?.translator || null,
        },
      );
      openTelephoneEncounter(te);
    },
    [userData, openTelephoneEncounter],
  );

  // Back-compat shim: older history entries (and any stragglers) that arrive
  // with the retired `telephoneEncounterFromCall` contract are forwarded into
  // the unified Encounter Review flow, telephone tab preselected.
  const handledCallTeRef = useRef(false);
  useEffect(() => {
    const ctx = (location.state as any)?.telephoneEncounterFromCall;
    if (!ctx?.callId || handledCallTeRef.current) return;
    handledCallTeRef.current = true;
    navigate(APP_ROUTES.process, {
      replace: true,
      state: {
        conversationId: ctx.callId,
        patientId: ctx.patientId ?? null,
        ehrPatientId: ctx.ehrPatientId ?? null,
        patientName: ctx.patientName ?? null,
        language: ctx.language ?? '',
        dialect: ctx.dialect ?? '',
        sessionType: TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
        preferredDocType: 'telephone',
        teAutoGenerate: true,
        initialProcessStep: 'note',
        fromPlanner: true,
      },
    });
  }, [location.state, navigate]);

  // Start a net-new blank telephone encounter — no originating call. This is
  // the dictation-first path for EHR-linked practices: the provider opens the
  // TE editor directly (patient picker → reason/message/assignee), dictates
  // the message, and pushes to ECW. Call-derived TEs still go through the
  // unified Encounter Review flow above.
  const handleCreateBlankTelephoneEncounter = useCallback(async () => {
    const te = await telephoneEncounterService.createBlankDraft({
      companyId: userData?.companyId || null,
      createdBy: userData?.userId || null,
      createdByName:
        providerNameFromUserData(userData) || userData?.displayName || null,
    });
    openTelephoneEncounter(te);
  }, [userData, openTelephoneEncounter]);

  // Start a solo dictation: a scribe-pipeline session with no patient audio,
  // tagged `dictation` server-side. Ends into Encounter Review, where the
  // transcript can be filed as a clinical note or (EHR-linked) a telephone
  // encounter. The patient can be linked during the session or at push time.
  const handleStartDictation = useCallback(() => {
    setSelectedSession(null);
    setSelectedEvent(null);
    navigate(SCRIBE_ROUTE, {
      state: {autoStart: true, dictation: true, fromPlanner: true},
    });
  }, [navigate]);

  // "Generate note" on a session detail: land on the unified Encounter
  // Review's template step, where ONE card offers the clinical-note /
  // telephone-encounter choice and one button launches it. Nothing is
  // preselected and nothing auto-generates — this is the chooser, not a
  // hand-off. The hint mirrors the senders' own EHR gating so the telephone
  // tab renders immediately (in-person interpreter sessions carry no phone
  // signal for the view to discover on its own).
  const handleDocumentSession = useCallback(
    (session: any) => {
      if (!session?.id) return;
      navigate(APP_ROUTES.process, {
        state: {
          conversationId: session.id,
          patientId: session.patient_id ?? null,
          ehrPatientId: session.ehr_patient_id ?? session.ehrPatientId ?? null,
          patientName: session.patient_name ?? null,
          language:
            session.foreign_language ||
            session.foreignLanguage ||
            session.language ||
            '',
          dialect: session.dialect || '',
          initialProcessStep: 'template',
          forceTemplateSelection: true,
          canCreateTelephoneEncounterHint: true,
          fromPlanner: true,
        },
      });
    },
    [navigate],
  );

  // Keep the selected planner event's embedded record in sync as the detail
  // view edits/pushes/closes the telephone encounter, so re-selecting reflects
  // the latest state.
  const handleTelephoneEncounterUpdated = useCallback((te: any) => {
    setSelectedEvent((prev: any) => {
      if (prev?.source !== 'telephoneEncounter') return prev;
      if (prev?.actions?.telephoneEncounter?.id !== te?.id) return prev;
      const next = plannerEventFromTelephoneEncounter(te);
      return next ? {...next, id: prev.id} : prev;
    });
  }, []);

  // After a telephone encounter is pushed and acknowledged, land the registrar
  // on the pushed patient's history — the same place a clinical-note push
  // returns them to. We rebuild a patient-selection event from the TE's linked
  // patient and clear any open session so the History list is what shows.
  const handleTelephoneEncounterPushed = useCallback((te: any) => {
    const patient = plannerEventFromTelephoneEncounter(te)?.patient;
    const patientItem = patient ? plannerPatientToPatientItem(patient) : null;
    const patientEvent = patientItem
      ? buildPlannerPatientSelectionEvent(patientItem, {
          source: 'telephoneEncounter',
        })
      : null;
    setSelectedSession(null);
    setSelectedEvent(patientEvent || null);
  }, []);

  const telephoneEncounterCurrentUser = useMemo(
    () => ({
      id: userData?.userId || null,
      name: providerNameFromUserData(userData) || userData?.displayName || null,
      role: userData?.role || null,
    }),
    [userData],
  );

  const handleNewEncounterScheduled = useCallback(() => {
    refreshSchedule?.();
    onNewEncounterClose();
  }, [onNewEncounterClose, refreshSchedule]);

  const handleSelectArchiveSession = useCallback((session: any) => {
    if (!session) return;
    setSelectedSession(session);
  }, []);

  const handleCloseSessionDetail = useCallback(() => {
    setSelectedSession(null);
    setSelectedEvent((prev: any) =>
      prev?.__plannerOpenEncounterDetail
        ? {...prev, __plannerOpenEncounterDetail: false}
        : prev,
    );
  }, []);

  const handleUnlinkedSessionPatientChanged = useCallback(
    (updatedSession: any, meta: Record<string, any> = {}) => {
      refreshSchedule?.();

      const patientEvent = buildPlannerPatientSelectionEvent(
        meta?.patientItem,
        {source: 'linkedEncounter'},
      );
      if (!patientEvent) return;

      setUnlinkedPatientSelectorRequest(null);
      setSelectedEvent(patientEvent);
      setSelectedSession(updatedSession || null);
    },
    [refreshSchedule],
  );

  const handleScheduledBotMutation = useCallback(() => {
    refreshSchedule?.();
    setSelectedSession(null);
    setSelectedEvent(null);
  }, [refreshSchedule]);

  const cancelHomeScrollAnimation = useCallback(() => {
    if (
      homeScrollAnimationRef.current !== null &&
      typeof window !== 'undefined' &&
      typeof window.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(homeScrollAnimationRef.current);
    }
    homeScrollAnimationRef.current = null;
  }, []);

  const animateHomeScrollTo = useCallback(
    (scrollController: any, targetTop: number) => {
      const startTop = scrollController.scrollTop || 0;
      const distance = targetTop - startTop;
      if (Math.abs(distance) <= PLANNER_HOME_SCROLL_REVEAL_THRESHOLD_PX) return;
      if (
        typeof window === 'undefined' ||
        typeof window.requestAnimationFrame !== 'function' ||
        typeof performance === 'undefined'
      ) {
        scrollController.scrollTop = targetTop;
        return;
      }

      cancelHomeScrollAnimation();
      const startedAt = performance.now();
      const step = (timestamp: number) => {
        const progress = Math.min(
          1,
          Math.max(
            0,
            (timestamp - startedAt) / PLANNER_HOME_SCROLL_DURATION_MS,
          ),
        );
        scrollController.scrollTop =
          startTop + distance * plannerHomeScrollEase(progress);
        if (progress < 1) {
          homeScrollAnimationRef.current = window.requestAnimationFrame(step);
          return;
        }
        scrollController.scrollTop = targetTop;
        homeScrollAnimationRef.current = null;
      };
      homeScrollAnimationRef.current = window.requestAnimationFrame(step);
    },
    [cancelHomeScrollAnimation],
  );

  const ensureHomeNewEncounterBottomVisible = useCallback(() => {
    if (selectedEvent) return;
    const scrollController = homeScrollControllerRef.current;
    const newEncounterSection = homeNewEncounterSectionRef.current;
    if (!scrollController || !newEncounterSection) return;
    if (
      typeof scrollController.getBoundingClientRect !== 'function' ||
      typeof newEncounterSection.getBoundingClientRect !== 'function'
    ) {
      return;
    }

    const scrollRect = scrollController.getBoundingClientRect();
    const sectionRect = newEncounterSection.getBoundingClientRect();
    const visibleBottom =
      typeof window === 'undefined'
        ? scrollRect.bottom
        : Math.min(scrollRect.bottom, window.innerHeight);
    const hiddenOverflow = sectionRect.bottom - visibleBottom;
    if (hiddenOverflow <= PLANNER_HOME_SCROLL_REVEAL_THRESHOLD_PX) return;

    const revealOverflow =
      hiddenOverflow + plannerHomeScrollTargetGapPx(scrollController);
    const nextScrollTop = Math.max(
      0,
      (scrollController.scrollTop || 0) + revealOverflow,
    );
    animateHomeScrollTo(scrollController, nextScrollTop);
  }, [animateHomeScrollTo, selectedEvent]);

  useEffect(() => {
    if (selectedEvent || typeof window === 'undefined') return undefined;

    let scheduledTimeout: number | null = null;
    const cancelScheduledEnsure = () => {
      if (scheduledTimeout !== null) {
        window.clearTimeout(scheduledTimeout);
        scheduledTimeout = null;
      }
    };
    const scheduleEnsure = (delay = PLANNER_HOME_SCROLL_SETTLE_DELAY_MS) => {
      cancelScheduledEnsure();
      scheduledTimeout = window.setTimeout(() => {
        scheduledTimeout = null;
        ensureHomeNewEncounterBottomVisible();
      }, delay);
    };

    scheduleEnsure(0);
    const handleWindowResize = () => scheduleEnsure(0);
    window.addEventListener('resize', handleWindowResize);

    const observedSection = homeNewEncounterSectionRef.current;
    homeNewEncounterSectionHeightRef.current =
      observedSection?.getBoundingClientRect?.().height ?? null;
    const resizeObserver =
      observedSection && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(entries => {
            const nextHeight =
              entries[0]?.contentRect?.height ??
              observedSection.getBoundingClientRect?.().height ??
              null;
            const previousHeight = homeNewEncounterSectionHeightRef.current;
            homeNewEncounterSectionHeightRef.current = nextHeight;

            if (previousHeight == null || nextHeight == null) return;
            if (
              nextHeight >
              previousHeight + PLANNER_HOME_SCROLL_REVEAL_THRESHOLD_PX
            ) {
              scheduleEnsure();
              return;
            }
            if (
              nextHeight <
              previousHeight - PLANNER_HOME_SCROLL_REVEAL_THRESHOLD_PX
            ) {
              cancelScheduledEnsure();
              cancelHomeScrollAnimation();
            }
          })
        : null;
    resizeObserver?.observe(observedSection);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      resizeObserver?.disconnect();
      cancelScheduledEnsure();
    };
  }, [
    cancelHomeScrollAnimation,
    ensureHomeNewEncounterBottomVisible,
    selectedEvent,
  ]);

  useEffect(
    () => () => {
      cancelHomeScrollAnimation();
    },
    [cancelHomeScrollAnimation],
  );

  const selectedScheduledBot =
    selectedEvent?.source === 'scheduledBot'
      ? selectedEvent.actions?.scheduledBot || null
      : null;
  const scheduledTodayPatientItems = useMemo(
    () => buildScheduledTodayPatientItems(sections),
    [sections],
  );

  const selectedPhoneCall =
    selectedEvent?.source === 'phoneCall'
      ? selectedEvent.actions?.phoneCall || null
      : null;

  const selectedTelephoneEncounter =
    selectedEvent?.source === 'telephoneEncounter'
      ? selectedEvent.actions?.telephoneEncounter || null
      : null;

  const selectedRecentEncounterSession =
    selectedEvent?.__plannerOpenEncounterDetail &&
    !selectedPhoneCall &&
    selectedPatient
      ? selectedEvent.actions?.resumeSession || null
      : null;

  const selectedUnlinkedSession =
    selectedEvent &&
    !selectedPhoneCall &&
    !selectedTelephoneEncounter &&
    !selectedPatient
      ? selectedEvent.actions?.resumeSession || null
      : null;
  const selectedUnlinkedSessionForDetail = useMemo(() => {
    if (!selectedUnlinkedSession) return null;
    const sessionId = selectedUnlinkedSession.id
      ? String(selectedUnlinkedSession.id)
      : '';
    const shouldOpenPatientSelector =
      sessionId &&
      unlinkedPatientSelectorRequest?.sessionId === sessionId &&
      unlinkedPatientSelectorRequest?.key;
    return shouldOpenPatientSelector
      ? {
          ...selectedUnlinkedSession,
          __openPatientSelectorKey: unlinkedPatientSelectorRequest.key,
        }
      : selectedUnlinkedSession;
  }, [selectedUnlinkedSession, unlinkedPatientSelectorRequest]);
  // Below lg the planner is a master→detail flow: opening a patient,
  // encounter, scheduled bot, or phone call swaps the list for the detail pane
  // (which has its own back/close control). Without that swap the detail would
  // render *below* the full-height list (off-screen) with a collapsed height —
  // so tapping appeared to do nothing. When nothing is selected the bands
  // Compact layouts show the iOS-style planner list as a full-height screen;
  // details push over it. Only `lg` becomes a two-pane workspace.
  const isDetailOpen = Boolean(selectedEvent);
  // The unlinked-encounter detail (rendered via PlannerUnlinkedDetailFrame) is
  // pushed full-bleed to the right edge so its scrollbar lives flush against
  // the panel edge instead of floating inside the content margin. The content
  // keeps its right inset via padding inside the scroller (see
  // PLANNER_DETAIL_EDGE_INSET below), so only the scrollbar moves outward.
  const isEncounterFrameDetail =
    isDetailOpen &&
    !selectedScheduledBot &&
    !selectedPhoneCall &&
    !selectedTelephoneEncounter &&
    !selectedPatient;

  const handleOpenUnlinkedPatientLink = useCallback(() => {
    const sessionId = selectedUnlinkedSession?.id
      ? String(selectedUnlinkedSession.id)
      : '';
    if (!sessionId) return;
    setUnlinkedPatientSelectorRequest({
      sessionId,
      key: Date.now(),
    });
  }, [selectedUnlinkedSession?.id]);

  return (
    <Box
      h="100%"
      minH={0}
      overflow="hidden"
      display="flex"
      flexDirection="column"
      bg={pageBg}
      data-testid="planner-workspace"
    >
      <Box
        pl={0}
        pr={0}
        pt={0}
        flex={1}
        minH={0}
        overflowY="hidden"
        overscrollBehavior="contain"
      >
        <Grid
          as="main"
          w="100%"
          h="100%"
          minH={0}
          sx={{
            '--planner-grid-gap': 'var(--chakra-space-10)',
            '--planner-fixed-content-width':
              'min(96rem, calc(100% - var(--chakra-space-8)))',
            '--planner-new-action-width': '44px',
            '--planner-left-column-width':
              'calc(clamp(20rem, 28vw, 28rem) + var(--planner-new-action-width))',
            '@media screen and (min-width: 80em)': {
              '--planner-grid-gap': 'var(--chakra-space-12)',
            },
          }}
          templateAreas={{
            base: isDetailOpen ? '"patientDetail"' : '"browserColumn"',
            lg: '"browserColumn patientDetail"',
          }}
          templateColumns={{
            base: '1fr',
            lg: 'minmax(0, var(--planner-left-column-width)) minmax(460px, 1fr)',
          }}
          templateRows={{
            base: 'minmax(0, 1fr)',
            lg: 'minmax(0, 1fr)',
          }}
          rowGap={0}
          columnGap={0}
          alignItems="stretch"
        >
          <PlannerBrowserColumn
            containerDisplay={{
              base: isDetailOpen ? 'none' : 'block',
              lg: 'block',
            }}
            compactHeader={
              <Box display={{base: 'block', lg: 'none'}} pt={2} pb={4}>
                <PlannerStartSummary
                  userData={userData}
                  sections={sections}
                  styles={styles}
                  itemProps={{}}
                />
              </Box>
            }
            sections={sections}
            scheduleScope={scheduleScope}
            isLoading={isScheduleLoading}
            hasLoaded={hasScheduleLoaded}
            error={scheduleError}
            isFallback={shouldUseScaffoldFallback}
            userData={userData}
            onSelect={handleSelectEvent}
            onSelectPatient={handleSelectPatient}
            onNewEncounter={handleCreateEncounter}
            onNewTelephoneEncounter={
              isEhrLinked ? handleCreateBlankTelephoneEncounter : undefined
            }
            onNewDictation={
              userData?.scribeDisabled !== true
                ? handleStartDictation
                : undefined
            }
            refreshSchedule={refreshSchedule}
            refreshEhrConnection={handleRefreshEhrConnection}
            encounterHistorySeed={encounterHistorySeed}
            recentsFeedRevision={recentsFeedRevision}
            deletedPatientIds={deletedPatientIds}
            selectedEventId={selectedEvent?.id || null}
            selectedPatientId={selectedPatient?.id || null}
            selectedPatient={selectedPatient}
          />

          <GridItem
            area="patientDetail"
            data-testid="planner-patient-detail"
            // Phones only mount this pane for a selected detail; the home
            // greeting/form never stacks under the schedule there.
            display={{base: isDetailOpen ? 'block' : 'none', lg: 'block'}}
            minW={0}
            minH={0}
            h="100%"
            w="100%"
            overflow={{base: isDetailOpen ? 'hidden' : undefined, lg: 'hidden'}}
            overscrollBehavior="contain"
            pt={{base: 4, lg: '4em'}}
            pl={{base: isDetailOpen ? 4 : 0, lg: 'clamp(2rem, 3vw, 4rem)'}}
            // For the encounter detail, drop the right padding on lg so the
            // scroller can reach the panel edge; the inset is re-added inside
            // the scroller instead (PLANNER_DETAIL_EDGE_INSET).
            pr={{
              base: isDetailOpen ? 4 : 0,
              lg: isEncounterFrameDetail ? 0 : 'clamp(2rem, 3vw, 4rem)',
            }}
            pb={0}
          >
            <Box
              ref={homeScrollControllerRef}
              w="100%"
              maxW={{
                base: PLANNER_CONTENT_MAX_WIDTH,
                lg: isEncounterFrameDetail
                  ? '100%'
                  : PLANNER_CONTENT_MAX_WIDTH_WIDE,
              }}
              mx="auto"
              h="100%"
              minH={0}
              position="relative"
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              overflowY={{base: isDetailOpen ? 'auto' : undefined, lg: 'auto'}}
              overscrollBehavior="contain"
              pb={!selectedEvent ? PLANNER_HOME_SCROLL_BOTTOM_GAP : 0}
              data-testid="planner-home-scroll-controller"
              sx={{
                ...scrollbarSx,
                scrollPaddingBottom: PLANNER_HOME_SCROLL_BOTTOM_GAP,
              }}
            >
              <AnimatePresence>
                {!selectedEvent && (
                  <MotionBox
                    key="planner-home"
                    // Compact layouts render the greeting in the browser list
                    // and launch the same form from the inline New action.
                    display={{base: 'none', lg: 'block'}}
                    flexShrink={0}
                    w="100%"
                    {...homeEntrance.container}
                  >
                    <Box flexShrink={0} pt={{base: 1, lg: 0}}>
                      <PlannerStartSummary
                        userData={userData}
                        sections={sections}
                        styles={styles}
                        itemProps={homeEntrance.item}
                      />
                      <MotionBox
                        ref={homeNewEncounterSectionRef}
                        display={{base: 'none', lg: 'block'}}
                        flexShrink={0}
                        w="100%"
                        data-testid="planner-home-new-encounter-section"
                        {...HOME_ENTRANCE_MOTION_DATA_ATTRS}
                        {...homeEntrance.item}
                      >
                        <CreateSessionForm
                          fromPlanner
                          initialPatientItems={scheduledTodayPatientItems}
                          onScheduled={refreshSchedule}
                        />
                      </MotionBox>
                    </Box>
                  </MotionBox>
                )}

                {selectedScheduledBot && (
                  <MotionBox
                    key={`scheduled-bot-${selectedEvent.id}`}
                    h="100%"
                    minH={0}
                    data-testid="planner-scheduled-bot-detail"
                    {...detailEntrance}
                  >
                    <Suspense
                      fallback={<ViewLoader label="Loading session…" />}
                    >
                      <ScheduledSessionDetailPanel
                        scheduledBot={selectedScheduledBot}
                        onBack={handleClearSelectedEvent}
                        onScheduledUpdated={handleScheduledBotMutation}
                        onScheduledCancelled={handleScheduledBotMutation}
                      />
                    </Suspense>
                  </MotionBox>
                )}

                {selectedPhoneCall && (
                  <MotionBox
                    key={`phone-call-${selectedPhoneCall.id || selectedEvent.id}`}
                    h="100%"
                    minH={0}
                    data-testid="planner-phone-call-detail"
                    {...detailEntrance}
                  >
                    <PhoneCallDetailPanel
                      call={selectedPhoneCall}
                      onBack={handleClearSelectedEvent}
                      onCreateTelephoneEncounter={
                        isEhrLinked
                          ? handleCreateTelephoneEncounterFromCall
                          : undefined
                      }
                    />
                  </MotionBox>
                )}

                {selectedTelephoneEncounter && (
                  <MotionBox
                    key={`telephone-encounter-${
                      selectedTelephoneEncounter.id || selectedEvent.id
                    }`}
                    h="100%"
                    minH={0}
                    data-testid="planner-telephone-encounter-detail"
                    {...detailEntrance}
                  >
                    <TelephoneEncounterDetailView
                      telephoneEncounter={selectedTelephoneEncounter}
                      companyId={userData?.companyId || null}
                      currentUser={telephoneEncounterCurrentUser}
                      onBack={handleClearSelectedEvent}
                      onUpdated={handleTelephoneEncounterUpdated}
                      onEditExisting={handleAdoptOpenTelephoneEncounter}
                      onPushed={handleTelephoneEncounterPushed}
                    />
                  </MotionBox>
                )}

                {selectedEvent &&
                  !selectedScheduledBot &&
                  !selectedPhoneCall &&
                  !selectedTelephoneEncounter &&
                  selectedPatient && (
                    <MotionBox
                      key={`patient-${selectedPatient.id || selectedEvent.id}`}
                      h="100%"
                      minH={0}
                      {...detailEntrance}
                    >
                      <PlannerPatientPanel
                        event={selectedEvent}
                        patient={selectedPatient}
                        sections={sections}
                        selectedSession={
                          selectedRecentEncounterSession || selectedSession
                        }
                        onClose={handleClearSelectedEvent}
                        onCloseSessionDetail={handleCloseSessionDetail}
                        onSelectSession={handleSelectArchiveSession}
                        onPatientUpdated={handlePatientUpdated}
                        onPatientDeleted={handlePatientDeleted}
                        onScheduled={refreshSchedule}
                        onSessionMutation={handleRecentsFeedMutation}
                        onDocumentCall={
                          isEhrLinked ? handleDocumentSession : undefined
                        }
                      />
                    </MotionBox>
                  )}

                {selectedEvent &&
                  !selectedScheduledBot &&
                  !selectedPhoneCall &&
                  !selectedPatient &&
                  selectedUnlinkedSession && (
                    <MotionBox
                      key={`unlinked-${selectedEvent.id}`}
                      h="100%"
                      minH={0}
                      data-testid="planner-unlinked-encounter-detail"
                      {...detailEntrance}
                    >
                      <PlannerUnlinkedDetailFrame
                        onLinkPatient={handleOpenUnlinkedPatientLink}
                        onClose={handleClearSelectedEvent}
                        styles={styles}
                        headerPr={{lg: PLANNER_DETAIL_EDGE_INSET}}
                      >
                        <Suspense
                          fallback={<ViewLoader label="Loading session…" />}
                        >
                          <SessionDetailPanel
                            session={selectedUnlinkedSessionForDetail}
                            patientItem={null}
                            plannerLayout
                            scrollerPr={{lg: PLANNER_DETAIL_EDGE_INSET}}
                            linkPatientSelectorTitle="Link Patient"
                            linkPatientSelectorShowInfoSection={false}
                            linkPatientSelectorActionsLabel={null}
                            linkPatientSelectorPatientTitle=""
                            linkPatientSelectorPatientDescription=""
                            linkPatientSelectorSubmitJustify="flex-end"
                            onSessionRemoved={handleRecentsFeedMutation}
                            onSessionRestored={handleRecentsFeedMutation}
                            onSessionPatientChanged={
                              handleUnlinkedSessionPatientChanged
                            }
                            onDocumentCall={
                              isEhrLinked ? handleDocumentSession : undefined
                            }
                          />
                        </Suspense>
                      </PlannerUnlinkedDetailFrame>
                    </MotionBox>
                  )}

                {selectedEvent &&
                  !selectedScheduledBot &&
                  !selectedPhoneCall &&
                  !selectedTelephoneEncounter &&
                  !selectedPatient &&
                  !selectedUnlinkedSession && (
                    <MotionBox
                      key={`unlinked-empty-${selectedEvent.id}`}
                      h="100%"
                      minH={0}
                      {...detailEntrance}
                    >
                      <PlannerUnlinkedDetailFrame
                        headerTitle={null}
                        onClose={handleClearSelectedEvent}
                        styles={styles}
                        headerPr={{lg: PLANNER_DETAIL_EDGE_INSET}}
                      >
                        <Box
                          h="100%"
                          minH={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          textAlign="center"
                          px={4}
                          data-testid="planner-unlinked-empty-detail"
                        >
                          <Text
                            as="h1"
                            color={styles.textColor}
                            fontSize={{base: 'xl', md: '2xl'}}
                            lineHeight="1.2"
                            fontWeight="bold"
                            sx={{fontWeight: '700 !important'}}
                          >
                            Unlinked Patient
                          </Text>
                        </Box>
                      </PlannerUnlinkedDetailFrame>
                    </MotionBox>
                  )}
              </AnimatePresence>
            </Box>
          </GridItem>
        </Grid>
      </Box>

      <Modal
        isOpen={isNewEncounterOpen}
        onClose={onNewEncounterClose}
        isCentered
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          data-testid="planner-new-encounter-modal"
          bg="transparent"
          boxShadow="none"
          maxW={{base: 'calc(100% - 2rem)', md: 'max(50vw, 600px)'}}
          mx={4}
        >
          <IconButton
            aria-label="Close"
            icon={<TablerX size={16} />}
            position="absolute"
            top={PLANNER_MODAL_CLOSE_BUTTON_TOP}
            right={CREATE_SESSION_CARD_PADDING}
            transform={`translateX(${PLANNER_MODAL_CLOSE_BUTTON_X_ALIGN_OFFSET})`}
            zIndex={1}
            boxSize={PLANNER_MODAL_CLOSE_BUTTON_SIZE}
            minW={PLANNER_MODAL_CLOSE_BUTTON_SIZE}
            p={0}
            bg="transparent"
            color={styles.subtleText}
            borderWidth="0"
            borderRadius="0"
            variant="ghost"
            onClick={onNewEncounterClose}
            _hover={{bg: 'transparent', color: styles.textColor}}
            _active={{bg: 'transparent'}}
            _focus={{bg: 'transparent'}}
            _focusVisible={styles.focusOutlineStyles}
          />
          <ModalBody p={0}>
            <CreateSessionForm
              key={`planner-new-encounter-${newEncounterFormKey}`}
              fromPlanner
              initialPatientItems={scheduledTodayPatientItems}
              onScheduled={handleNewEncounterScheduled}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PlannerPage;
